import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useT, type Lang } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";


const LANGS: { code: Lang; label: string }[] = [
  { code: "cs", label: "CS" },
  { code: "pl", label: "PL" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
  { code: "pt", label: "PT" },
  { code: "fr", label: "FR" },
  { code: "hr", label: "HR" },
  { code: "sk", label: "SK" },
  { code: "ro", label: "RO" },
  { code: "it", label: "IT" },
  { code: "hu", label: "HU" },
  { code: "bg", label: "BG" },
];

function usePublicRoutes(t: (k: string) => string) {
  return [
    { to: "/", label: t("nav.home") },
    { to: "/mapa", label: t("nav.mapa") },
    { to: "/registrace-podniku", label: t("nav.proMajitele") },
    { to: "/moje-podniky", label: t("nav.mojePodniky") },
    { to: "/majitele-vacantu", label: t("nav.vakanty") },
    { to: "/auth", label: t("nav.prihlasit") },
  ];
}

export function QaOverlay() {
  const { lang, setLang, t, qa } = useT();
  const { hasRole, loading } = useAuth();
  const [open, setOpen] = useState(true);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const publicRoutes = usePublicRoutes(t);

  // QA overlay is a staff-only tool — hide entirely for everyone else.
  if (loading || !hasRole("admin")) return null;


  if (!qa.enabled) {
    return (
      <button
        onClick={() => qa.setEnabled(true)}
        className="fixed bottom-3 right-3 z-[9999] rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white shadow-lg hover:bg-black"
        title="Zapnout QA režim"
      >
        QA
      </button>
    );
  }

  return (
    <div className="fixed bottom-3 right-3 z-[9999] w-[320px] max-w-[calc(100vw-1rem)] rounded-lg border border-black/10 bg-white text-foreground shadow-2xl">
      <div className="flex items-center justify-between border-b border-black/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          <strong className="text-sm">QA režim</strong>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase">{lang}</span>
        </div>
        <div className="flex gap-1">
          <button
            className="rounded px-2 py-0.5 text-xs hover:bg-muted"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "−" : "+"}
          </button>
          <button
            className="rounded px-2 py-0.5 text-xs hover:bg-muted"
            onClick={() => qa.setEnabled(false)}
            title="Vypnout QA"
          >
            ×
          </button>
        </div>
      </div>

      {open && (
        <div className="max-h-[70vh] space-y-3 overflow-auto p-3 text-sm">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Jazyk</div>
            <div className="flex flex-wrap gap-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { qa.reset(); setLang(l.code); }}
                  className={`rounded border px-2 py-1 text-xs ${
                    lang === l.code
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-black/15 bg-white hover:bg-muted"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Obrazovky</div>
            <ul className="space-y-0.5">
              {publicRoutes.map((r: { to: string; label: string }) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    className={`block rounded px-2 py-1 text-xs hover:bg-muted ${
                      pathname === r.to ? "bg-muted font-semibold" : ""
                    }`}
                    onClick={() => qa.reset()}
                  >
                    {r.label} <span className="font-mono text-muted-foreground">{r.to}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Chybějící překlady ({qa.misses.length})
              </span>
              <button
                onClick={qa.reset}
                className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] hover:bg-muted"
              >
                Reset
              </button>
            </div>
            {qa.misses.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Žádné — nebo přepněte jazyk a klikejte po obrazovce. Klíče vykreslené v aktuálním
                jazyce, které spadnou na češtinu, se zde objeví označené ⚑.
              </p>
            ) : (
              <ul className="space-y-1">
                {qa.misses.map((m, i) => (
                  <li key={`${m.key}-${i}`} className="rounded bg-muted/50 px-2 py-1 font-mono text-[11px]">
                    <span
                      className={`mr-1 inline-block rounded px-1 text-[9px] font-bold uppercase ${
                        m.kind === "missing" ? "bg-red-600 text-white" : "bg-amber-500 text-white"
                      }`}
                    >
                      {m.kind === "missing" ? "MISS" : "CS"}
                    </span>
                    {m.key}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground">
            Tip: texty bez překladu jsou v UI označené ⚑. Přepněte jazyk, projděte obrazovky
            a sledujte tento seznam.
          </p>
        </div>
      )}
    </div>
  );
}
